//@flow
import * as React from 'react';
import { themr } from 'react-css-themr';
import { compose } from 'recompose';
import { translate } from 'react-i18next';

import OverlayDialog from 'common/components/core/dialog/OverlayDialog';
import { HeaderAppBar } from 'common/components/core/app_bar/HeaderAppBar';
import {
    Button,
    ButtonTypes,
    ButtonWithPopover
} from 'common/components/core/button';
import theme from 'common/components/core/dialog/ConfirmationDialog.scss';
import { dashboardColors } from 'common/enums/ColorEnum';
import { Text, Icon, Columns, Column, Stack } from '@locus-taxy/neo-ui';

/*========================================
=            Type definitions            =
========================================*/
type Props = {
    active: boolean,
    theme: Object,
    title: string | any,
    headerColor?: string,
    acceptActionLabel?: string,
    acceptActionTooltip?: ?string,
    cancelActionLabel?: string,
    isLoading?: boolean,
    onCancel: () => void,
    onAccept: () => void
};

type confirmationDialogFactoryProps = {
    InnerContent?: Function | React.ComponentType<any>,
    Actions?: Function | React.ComponentType<any>,
    options: Object
};
/*=====  End of Type definitions  ======*/

const DefaultInnerContent = ({
    t,
    innerText = t('translations:youSure'),
    innerSubText = '',
    info
}) => {
    return (
        <Stack space="small">
            <Column>
                <Text>{innerText}</Text>
                {innerSubText ? <Text>{innerSubText}</Text> : null}
            </Column>
            {info ? (
                <Columns space="xSmall" alignY="center">
                    <Icon
                        size={Icon.Size.medium}
                        icon={info.icon}
                        color={info.color}
                    />
                    <Text color={info.color}>{info.text}</Text>
                </Columns>
            ) : null}
        </Stack>
    );
};

const DefaultActions = ({
    onCancel,
    onAccept,
    t,
    acceptActionColor,
    acceptActionLabel = t('yesDelete'),
    cancelActionLabel = t('cancel'),
    isLoading,
    acceptActionTooltip,
    theme,
    confirmButtonTestId
}) => {
    return (
        <div className={theme.actions}>
            {onCancel ? (
                <Button
                    key={'cancelAction'}
                    flat
                    semibold
                    uppercase
                    label={cancelActionLabel}
                    theme={theme}
                    buttonType={ButtonTypes.Text}
                    onClick={onCancel}
                />
            ) : null}
            {onAccept ? (
                <ButtonWithPopover
                    key={'acceptAction'}
                    flat
                    semibold
                    uppercase
                    smallLoader
                    color={acceptActionColor}
                    label={acceptActionLabel}
                    theme={theme}
                    buttonType={ButtonTypes.Text}
                    isLoading={isLoading}
                    disabled={isLoading}
                    onClick={onAccept}
                    isPopOverOpen={!!acceptActionTooltip}
                    popOverContent={acceptActionTooltip}
                    testId={confirmButtonTestId}
                />
            ) : null}
        </div>
    );
};

const confirmationDialogFactory = ({
    InnerContent = DefaultInnerContent,
    Actions = DefaultActions,
    options = {}
}: confirmationDialogFactoryProps) => {
    const { undecorated = false } = options;
    class ConfirmationDialog extends React.PureComponent<Props, void> {
        props: Props;
        static defaultProps = {
            acceptActionColor: dashboardColors.salmonTwo.name
        };
        render() {
            const { active, theme, headerColor, onCancel, title } = this.props;
            const dialogCloseActionIcon = (
                <Button
                    inverted
                    icon="cancel-thin"
                    buttonType={ButtonTypes.Icon}
                    onClick={onCancel}
                    ripple={false}
                />
            );
            return (
                <OverlayDialog
                    active={active}
                    theme={theme}
                    onEscapeClick={onCancel}
                    dialogClassName={theme.confirmationDialog}
                >
                    <HeaderAppBar
                        flat
                        headerColor={headerColor}
                        title={title}
                        theme={theme}
                        actions={dialogCloseActionIcon}
                    />
                    <div className={theme.contentWrap}>
                        <InnerContent {...this.props} />
                    </div>
                    <div className={theme.actionsWrap}>
                        <Actions {...this.props} />
                    </div>
                </OverlayDialog>
            );
        }
    }

    return undecorated
        ? ConfirmationDialog
        : compose(
              themr('ConfirmationDialog', theme),
              translate('configure')
          )(ConfirmationDialog);
};

const ConfirmationDialog = confirmationDialogFactory({
    InnerContent: DefaultInnerContent,
    Actions: DefaultActions,
    options: {}
});
export default ConfirmationDialog;
export { confirmationDialogFactory, ConfirmationDialog };
